// Help sheet (CONTRACT "Keyboard model", "Overlays"): the "?" dialog. 560 px,
// wall step, scrim under it, no motion (keyboard initiated). Focus is trapped
// inside, Escape closes, focus returns to the opener. Content is a table of
// shortcut rows; the caller passes the shortcuts so every destination lists
// its own keys.
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from './Button';
import { KeyCap } from './KeyCap';
import { cx } from './cx';
import { trapFocus } from './focusTrap';
import './HelpSheet.css';

export interface ShortcutRow {
  readonly keys: ReadonlyArray<string>;
  readonly action: string;
  readonly scope?: string;
}

export interface HelpSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title?: string;
  readonly intro?: ReactNode;
  readonly shortcuts: ReadonlyArray<ShortcutRow>;
  readonly className?: string;
}

export function HelpSheet({ open, onClose, title = 'Keyboard', intro, shortcuts, className }: HelpSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open || !sheetRef.current) return undefined;
    // The dialog itself takes focus, not its close control: opened from "?"
    // with the close button focused, a screen reader announced "Close help,
    // button" and never the sheet's own title. Tab from here reaches Close
    // first, exactly as before.
    return trapFocus(sheetRef.current, { onEscape: onClose, initialFocus: sheetRef.current });
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="help-scrim" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div ref={sheetRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={cx('help', className)}>
        <div className="help-h">
          <h2 id={titleId} className="help-t">{title}</h2>
          <Button variant="icon" size="sm" aria-label="Close help" icon={<X />} onClick={onClose} />
        </div>
        {intro ? <p className="help-p">{intro}</p> : null}
        <table className="help-tbl">
          <tbody>
            {shortcuts.map((row) => (
              <tr key={`${row.keys.join('+')}:${row.action}`}>
                <td className="help-keys">
                  <span className="help-keys-in">
                    {row.keys.map((key) => <KeyCap key={key}>{key}</KeyCap>)}
                  </span>
                </td>
                <td>
                  {row.action}
                  {row.scope ? <span className="help-scope">{row.scope}</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
